// 현장일지 댓글(`0129` log_comment) — 누가 어느 일지에 남길 수 있는지·행 모양은 저장소 밖 한 벌(인메모리·Supabase 공통).

import { z } from "zod";

import { CheckViolationError } from "../../lib/check-violation";
import { isCustomerVisibleLog } from "./customer-visible";
import type { ProjectLog } from "./types";

export const LOG_COMMENT_AUTHOR_KINDS = ["MEMBER", "CUSTOMER"] as const;
export type LogCommentAuthorKind = (typeof LOG_COMMENT_AUTHOR_KINDS)[number];

export const MAX_LOG_COMMENT_BODY = 1000;

export interface LogComment {
  id: string;
  org_id: string;
  project_id: string;
  log_id: string;
  author_kind: LogCommentAuthorKind;
  // 직원 글만 사람을 가리킨다 — 직원이 나가면 비워진다(on delete set null).
  member_id: string | null;
  body: string;
  created_at: string;
}

export const LOG_COMMENT_EMPTY_MESSAGE = "댓글 내용을 적어 주세요";
export const LOG_COMMENT_TOO_LONG_MESSAGE = `댓글은 ${MAX_LOG_COMMENT_BODY}자까지 쓸 수 있습니다`;
export const LOG_COMMENT_TARGET_MESSAGE = "댓글을 남길 수 없는 현장일지입니다";
export const LOG_COMMENT_RATE_MESSAGE = "댓글이 너무 잦습니다. 잠시 뒤 다시 남겨 주세요";

// PG 의 char_length 는 코드포인트를 센다 · btrim 은 공백만 턴다(0129 log_comment_body_len 과 같은 셈).
const pgLength = (value: string): number => Array.from(value).length;
const pgBtrim = (value: string): string => value.replace(/^ +| +$/g, "");

// 본문 — 앞뒤 공백을 털고 1~1000자.
export const logCommentBodySchema = z
  .string({ message: LOG_COMMENT_EMPTY_MESSAGE })
  .transform((value) => value.trim())
  .refine((value) => value.length > 0, { message: LOG_COMMENT_EMPTY_MESSAGE })
  .refine((value) => pgLength(value) <= MAX_LOG_COMMENT_BODY, { message: LOG_COMMENT_TOO_LONG_MESSAGE });

// 댓글을 받을 수 없는 일지(없음·다른 현장·고객에게 안 보이는 구분) — 화면은 문구가 아니라 이 타입으로 가른다.
export class LogCommentTargetError extends Error {
  constructor() {
    super(LOG_COMMENT_TARGET_MESSAGE);
    this.name = "LogCommentTargetError";
  }
}

export const isLogCommentTargetError = (error: unknown): error is LogCommentTargetError =>
  error instanceof LogCommentTargetError;

// 고객 링크 댓글이 분당 한도에 걸렸다 — 잠그지 않고 잠시 뒤 다시.
export class CustomerLogCommentBlockedError extends Error {
  constructor() {
    super(LOG_COMMENT_RATE_MESSAGE);
    this.name = "CustomerLogCommentBlockedError";
  }
}

export const isCustomerLogCommentBlocked = (error: unknown): error is CustomerLogCommentBlockedError =>
  error instanceof CustomerLogCommentBlockedError;

type CommentTarget = Pick<ProjectLog, "id" | "org_id" | "project_id" | "kind">;

// 직원 — 그 현장의 일지면 구분과 상관없이 남긴다.
export const staffCommentTargetOk = (log: CommentTarget | null, projectId: string): log is CommentTarget =>
  log !== null && log.project_id === projectId;

// 고객 — 토큰 현장의 일지 중 고객에게 보이는 구분(CUSTOMER_VISIBLE_LOG_KINDS)에만.
export const customerCommentTargetOk = (log: CommentTarget | null, projectId: string): log is CommentTarget =>
  log !== null && log.project_id === projectId && isCustomerVisibleLog(log.kind);

// 새 댓글 한 줄 — id·시각은 앱이 정한다 · 고객 글은 사람을 가리키지 않는다.
export const buildLogCommentRow = (args: {
  id: string;
  log: Pick<ProjectLog, "id" | "org_id" | "project_id">;
  author: { kind: "MEMBER"; memberId: string } | { kind: "CUSTOMER" };
  body: string;
  at: string;
}): LogComment => ({
  id: args.id,
  org_id: args.log.org_id,
  project_id: args.log.project_id,
  log_id: args.log.id,
  author_kind: args.author.kind,
  member_id: args.author.kind === "MEMBER" ? args.author.memberId : null,
  body: args.body.trim(),
  created_at: args.at,
});

// 쓴 순서(오래된 것 먼저) — 같은 시각이면 id.
export const compareLogComments = (a: LogComment, b: LogComment): number =>
  a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;

// 일지 id → 댓글 수 — 직원 일지 목록이 한 번에 센다.
export const commentCountsOf = (comments: readonly Pick<LogComment, "log_id">[]): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const c of comments) counts[c.log_id] = (counts[c.log_id] ?? 0) + 1;
  return counts;
};

export interface LogCommentItem {
  id: string;
  log_id: string;
  author_kind: LogCommentAuthorKind;
  author_name: string | null;
  body: string;
  created_at: string;
}

// 화면 값 — 사람 id·조직·현장은 떨구고 이름만(나간 직원·고객 글은 null).
export const logCommentItemOf = (comment: LogComment, memberNames: ReadonlyMap<string, string>): LogCommentItem => ({
  id: comment.id,
  log_id: comment.log_id,
  author_kind: comment.author_kind,
  author_name: comment.member_id ? memberNames.get(comment.member_id) ?? null : null,
  body: comment.body,
  created_at: comment.created_at,
});

// `0129` check·트리거를 그대로 — 인메모리 저장소가 쓰기 직전에 불러 DB 와 같은 이름으로 튕긴다.
export const assertLogCommentRow = (
  row: LogComment,
  log: Pick<ProjectLog, "org_id" | "project_id"> | undefined,
  member: { org_id: string } | null
): void => {
  if (!LOG_COMMENT_AUTHOR_KINDS.includes(row.author_kind)) throw new CheckViolationError("log_comment_author_kind_check", "log_comment_author_kind_check");
  if (row.author_kind !== "MEMBER" && row.member_id !== null) throw new CheckViolationError("log_comment_author_member", "log_comment_author_member");
  const n = pgLength(pgBtrim(row.body));
  if (n < 1 || n > MAX_LOG_COMMENT_BODY) throw new CheckViolationError("log_comment_body_len", "log_comment_body_len");
  const logOk = log !== undefined && log.org_id === row.org_id && log.project_id === row.project_id;
  const memberOk = row.author_kind !== "MEMBER" || (member !== null && member.org_id === row.org_id);
  if (!logOk || !memberOk) throw new CheckViolationError("log_comment_guard", "log_comment_guard");
};
